/** Preferencje zgody na pliki cookie (RODO / ePrivacy). */
export type CookieCategory = 'necessary' | 'functional' | 'analytics' | 'marketing';

export type CookieConsentChoices = {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

export type StoredCookieConsent = CookieConsentChoices & {
  version: 2;
  updatedAt: string;
};

export const COOKIE_CONSENT_STORAGE_KEY = 'fk_cookie_consent_v2';
const LEGACY_CONSENT_KEY = 'fk_cookie_consent_v1';

export const COOKIE_SETTINGS_EVENT = 'fk:open-cookie-settings';

const DEFAULT_CHOICES: CookieConsentChoices = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

export const COOKIE_CATEGORY_META: ReadonlyArray<{
  id: CookieCategory;
  title: string;
  description: string;
  required?: boolean;
}> = [
  {
    id: 'necessary',
    title: 'Niezbędne',
    description:
      'Logowanie i sesja (Supabase), bezpieczeństwo oraz podstawowe działanie strony i panelu. Bez nich serwis nie działa poprawnie.',
    required: true,
  },
  {
    id: 'functional',
    title: 'Funkcjonalne',
    description:
      'Zapamiętywanie ustawień w przeglądarce (np. postęp w podglądzie kursów, przeczytane aktualności, cache wykresu na stronie głównej).',
  },
  {
    id: 'analytics',
    title: 'Analityczne',
    description:
      'Google Analytics — anonimowe statystyki odwiedzin i nawigacji (ładowane dopiero po Twojej zgodzie).',
  },
  {
    id: 'marketing',
    title: 'Marketingowe',
    description:
      'Tagi reklamowe i pomiar konwersji (np. Google Ads). Obecnie nieużywane; możesz włączyć na przyszłość, jeśli je dodamy.',
  },
];

function parseStored(raw: string | null): StoredCookieConsent | null {
  if (!raw) {
    return null;
  }
  try {
    const data = JSON.parse(raw) as Partial<StoredCookieConsent>;
    if (data.version !== 2) {
      return null;
    }
    return {
      version: 2,
      necessary: true,
      functional: Boolean(data.functional),
      analytics: Boolean(data.analytics),
      marketing: Boolean(data.marketing),
      updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

function migrateLegacyConsent(): StoredCookieConsent | null {
  try {
    if (window.localStorage.getItem(LEGACY_CONSENT_KEY) !== 'accepted') {
      return null;
    }
    const migrated: StoredCookieConsent = {
      version: 2,
      necessary: true,
      functional: true,
      analytics: true,
      marketing: false,
      updatedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(migrated));
    window.localStorage.removeItem(LEGACY_CONSENT_KEY);
    return migrated;
  } catch {
    return null;
  }
}

export function getCookieConsent(): StoredCookieConsent | null {
  try {
    return parseStored(window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY)) ?? migrateLegacyConsent();
  } catch {
    return null;
  }
}

export function hasCookieConsentAnswered(): boolean {
  return getCookieConsent() !== null;
}

export function getCookieChoices(): CookieConsentChoices {
  return getCookieConsent() ?? DEFAULT_CHOICES;
}

export function hasCategoryConsent(category: CookieCategory): boolean {
  const choices = getCookieChoices();
  if (category === 'necessary') {
    return true;
  }
  return Boolean(choices[category]);
}

export function saveCookieConsent(choices: CookieConsentChoices): void {
  const stored: StoredCookieConsent = {
    version: 2,
    necessary: true,
    functional: choices.functional,
    analytics: choices.analytics,
    marketing: choices.marketing,
    updatedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* private mode */
  }
}

export function getDefaultCookieChoices(): CookieConsentChoices {
  return { ...DEFAULT_CHOICES };
}

export function getAcceptAllChoices(): CookieConsentChoices {
  return {
    necessary: true,
    functional: true,
    analytics: true,
    marketing: false,
  };
}

export function getNecessaryOnlyChoices(): CookieConsentChoices {
  return { ...DEFAULT_CHOICES };
}

/** Otwiera panel ustawień cookie (np. link w stopce). */
export function openCookieSettings(): void {
  window.dispatchEvent(new CustomEvent(COOKIE_SETTINGS_EVENT));
}
